import { useCallback, useEffect, useRef, useState } from 'react';
import type { MigrationAnswer, MigrationEvent, MigrationJob, MigrationRecoveryAction } from '../types/modpackMigration';
import { modpackMigrationApi } from '../services/modpackMigrationApi';
import { mergeUpdaterEvents } from '../utils/modpackUpdater';
import { migrationActive, migrationCanCancel } from '../utils/modpackMigration';

const message = (error: unknown) => error instanceof Error ? error.message : 'The migration service could not be reached.';

/** Job identity stays stable when the config moves to another host. No browser state owns the migration. */
export function useMigrationJob(selected: string | null, canWrite: boolean) {
  const [job, setJob] = useState<MigrationJob | null>(null);
  const [events, setEvents] = useState<MigrationEvent[]>([]);
  const [pollError, setPollError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [clock, setClock] = useState(Date.now());
  const mutationPending = useRef(false);
  const generation = useRef(0);
  const answered = useRef(new Set<string>());
  useEffect(() => { const interval = setInterval(() => setClock(Date.now()), 5000); return () => clearInterval(interval); }, []);
  useEffect(() => { setMutationError(null); }, [job?.pending_request?.id]);
  useEffect(() => {
    generation.current += 1;
    setJob(null); setEvents([]); setLastSync(null); setPollError(null); setMutationError(null);
    if (!selected) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    let cursor = 0;
    const poll = async () => {
      let delay = 1800;
      try {
        const [current, history] = await Promise.all([
          modpackMigrationApi.job(selected, controller.signal), modpackMigrationApi.events(selected, cursor, controller.signal),
        ]);
        if (controller.signal.aborted) return;
        setJob(current.job.pending_request && answered.current.has(current.job.pending_request.id)
          ? { ...current.job, pending_request: null } : current.job);
        setEvents(previous => mergeUpdaterEvents(previous, history.events));
        for (const event of history.events) cursor = Math.max(cursor, event.sequence);
        if (history.events.length && cursor < Math.max(current.job.last_sequence, history.last_sequence ?? 0)) delay = 50;
        else if (!migrationActive(current.job.state)) delay = 5000;
        setPollError(null); setLastSync(Date.now());
      } catch (error) { if (!controller.signal.aborted) setPollError(message(error)); }
      finally { if (!controller.signal.aborted) timer = setTimeout(poll, delay); }
    };
    void poll();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [selected]);
  const fresh = !!lastSync && clock - lastSync < 20000 && !pollError;
  const answer = useCallback(async (requestId: string, value: MigrationAnswer) => {
    if (!job || !canWrite || mutationPending.current || !fresh || job.cancel_requested || job.state !== 'waiting'
      || job.pending_request?.id !== requestId || answered.current.has(requestId)) throw new Error('Wait for this exact request and fresh migration progress before answering.');
    const ownGeneration = generation.current;
    mutationPending.current = true; setBusy(true); setMutationError(null);
    try {
      const result = await modpackMigrationApi.answer(job.id, requestId, value);
      if (result.accepted !== true) throw new Error('The answer was not acknowledged. Refresh this migration before trying again.');
      answered.current.add(requestId);
      if (ownGeneration === generation.current) setJob(result.job);
    } catch (error) { if (ownGeneration === generation.current) setMutationError(message(error)); throw error; }
    finally { mutationPending.current = false; setBusy(false); }
  }, [job, canWrite, fresh]);
  const control = async (operation: 'cancel' | MigrationRecoveryAction) => {
    if (!job || !canWrite || mutationPending.current || !fresh) throw new Error('Wait for fresh migration progress before continuing.');
    if (operation === 'cancel' ? !migrationCanCancel(job.state) || job.cancel_requested : !job.recovery_actions?.includes(operation))
      throw new Error('This recovery action is no longer available. Refresh the migration.');
    const ownGeneration = generation.current;
    mutationPending.current = true; setBusy(true); setMutationError(null);
    try {
      const result = operation === 'cancel' ? await modpackMigrationApi.cancel(job.id) : await modpackMigrationApi.recover(job.id, operation);
      if (ownGeneration === generation.current) setJob(result.job);
      return result.job;
    } catch (error) { if (ownGeneration === generation.current) setMutationError(message(error)); throw error; }
    finally { mutationPending.current = false; setBusy(false); }
  };
  return { job, events, pollError, mutationError, lastSync, busy, fresh, answer, control };
}
